import { useCallback, useEffect, useState } from 'react'
import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import Chip from '@mui/joy/Chip'
import CircularProgress from '@mui/joy/CircularProgress'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Snackbar from '@mui/joy/Snackbar'
import Stack from '@mui/joy/Stack'
import Switch from '@mui/joy/Switch'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import { PARTNER_OFFER_PENDING_STALE_MS, type PartnerOfferApplicationRow, type PartnerOfferStatus } from '@numeo/types-main-mobile'
import { partnerOfferDeeplink } from '@numeo/types'
import { partnerOffersApi, type PartnerOfferSummary } from 'api/partnerOffers'

const PAGE_SIZE = 50

const STATUS_COPY: Record<PartnerOfferStatus, { label: string; color: 'neutral' | 'success' | 'danger' | 'warning' }> = {
    pending: { label: 'Sending', color: 'warning' },
    in_review: { label: 'In review', color: 'success' },
    failed: { label: 'Failed', color: 'danger' },
}

const formatDate = (iso: string): string => new Date(iso).toLocaleString()

interface SnackbarState {
    open: boolean
    message: string
    color: 'success' | 'danger'
}

/** What an operator sees on a row left at `pending`: only a call that was made and has gone quiet can be decided, so it cannot race the user's own retry. */
function PendingActions({
    partnerName,
    row,
    onDecide,
}: {
    partnerName: string
    row: PartnerOfferApplicationRow
    onDecide: (status: 'in_review' | 'failed') => void
}) {
    if (!row.sentAt) {
        return (
            <Chip size="sm" variant="outlined" color="neutral">
                Never sent: the user can send it again
            </Chip>
        )
    }
    const quiet = Date.now() - new Date(row.updatedAt).getTime() > PARTNER_OFFER_PENDING_STALE_MS
    if (!quiet) {
        return (
            <Chip size="sm" variant="outlined" color="warning">
                Sending
            </Chip>
        )
    }
    return (
        <Stack direction="row" spacing={0.5} alignItems="center">
            <Chip size="sm" variant="outlined" color="warning">
                Call made, outcome unknown
            </Chip>
            <Button size="sm" variant="soft" color="success" onClick={() => onDecide('in_review')}>
                {partnerName} has it
            </Button>
            <Button size="sm" variant="soft" color="danger" onClick={() => onDecide('failed')}>
                Not received
            </Button>
        </Stack>
    )
}

function ApplicationsTable({
    partnerName,
    offerKey,
    fieldKeys,
    refreshToken,
    onResolved,
}: {
    partnerName: string
    offerKey: string
    fieldKeys: { key: string; label: string }[]
    refreshToken: number
    onResolved: (message: string, ok: boolean) => void
}) {
    const [rows, setRows] = useState<PartnerOfferApplicationRow[]>([])
    const [status, setStatus] = useState<PartnerOfferStatus | 'all'>('all')
    const [loading, setLoading] = useState(true)
    const [failed, setFailed] = useState(false)
    const [hasMore, setHasMore] = useState(false)

    const load = useCallback(
        async (append: boolean, before?: string) => {
            setLoading(true)
            setFailed(false)
            try {
                const page = await partnerOffersApi.applications(offerKey, { status: status === 'all' ? undefined : status, before, limit: PAGE_SIZE })
                setRows((prev) => (append ? [...prev, ...page] : page))
                setHasMore(page.length === PAGE_SIZE)
            } catch (error) {
                console.error('Failed to load partner applications', error)
                setFailed(true)
            } finally {
                setLoading(false)
            }
        },
        [offerKey, status]
    )

    useEffect(() => {
        void load(false)
    }, [load, refreshToken])

    async function resolve(row: PartnerOfferApplicationRow, status: 'in_review' | 'failed') {
        try {
            await partnerOffersApi.resolve(offerKey, row.id, status)
            onResolved(status === 'in_review' ? 'Marked as in review' : 'Marked as failed: the user can send it again', true)
            await load(false)
        } catch (error) {
            console.error('Failed to resolve the application', error)
            onResolved('Could not update the application. It may already be settled: refresh.', false)
        }
    }

    return (
        <Box sx={{ mt: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                <Typography level="title-md">Applications</Typography>
                <Select size="sm" value={status} onChange={(_event, value) => value && setStatus(value)} sx={{ minWidth: 140 }}>
                    <Option value="all">All statuses</Option>
                    <Option value="in_review">In review</Option>
                    <Option value="failed">Failed</Option>
                    <Option value="pending">Sending</Option>
                </Select>
            </Stack>

            {failed ? (
                <Alert
                    color="danger"
                    endDecorator={
                        <Button size="sm" variant="soft" color="danger" onClick={() => void load(false)}>
                            Retry
                        </Button>
                    }
                >
                    Could not load the applications.
                </Alert>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="sm" stickyHeader>
                        <thead>
                            <tr>
                                <th>Sent</th>
                                <th>Status</th>
                                <th>Account</th>
                                <th>Company (project)</th>
                                <th />
                                {fieldKeys.map((field) => (
                                    <th key={field.key}>{field.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.id}>
                                    <td>{formatDate(row.createdAt)}</td>
                                    <td>
                                        <Chip size="sm" variant="soft" color={STATUS_COPY[row.status].color}>
                                            {STATUS_COPY[row.status].label}
                                        </Chip>
                                    </td>
                                    <td>{row.userEmail}</td>
                                    <td>{row.projectName}</td>
                                    <td>
                                        {row.status === 'pending' ? (
                                            <PendingActions partnerName={partnerName} row={row} onDecide={(status) => void resolve(row, status)} />
                                        ) : null}
                                    </td>
                                    {fieldKeys.map((field) => (
                                        <td key={field.key}>{row.values[field.key] ?? ''}</td>
                                    ))}
                                </tr>
                            ))}
                            {!loading && rows.length === 0 ? (
                                <tr>
                                    <td colSpan={5 + fieldKeys.length}>
                                        <Typography level="body-sm" sx={{ py: 2, textAlign: 'center' }}>
                                            No applications yet.
                                        </Typography>
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    </Table>
                </Box>
            )}

            <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                {loading ? (
                    <CircularProgress size="sm" />
                ) : hasMore ? (
                    <Button size="sm" variant="outlined" onClick={() => void load(true, rows[rows.length - 1]?.createdAt)}>
                        Load more
                    </Button>
                ) : null}
            </Stack>
        </Box>
    )
}

export default function MobilePartnerOffersPage() {
    const [offers, setOffers] = useState<PartnerOfferSummary[] | null>(null)
    const [loadFailed, setLoadFailed] = useState(false)
    const [saving, setSaving] = useState<string | null>(null)
    const [refreshToken, setRefreshToken] = useState(0)
    const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', color: 'success' })

    const load = useCallback(async () => {
        setLoadFailed(false)
        try {
            setOffers(await partnerOffersApi.list())
        } catch (error) {
            console.error('Failed to load partner offers', error)
            setLoadFailed(true)
        }
    }, [])

    useEffect(() => {
        void load()
    }, [load])

    async function toggle(offer: PartnerOfferSummary, enabled: boolean) {
        setSaving(offer.definition.key)
        try {
            await partnerOffersApi.setEnabled(offer.definition.key, enabled)
            setOffers((prev) => prev?.map((item) => (item.definition.key === offer.definition.key ? { ...item, enabled } : item)) ?? prev)
            setSnackbar({ open: true, message: enabled ? 'Offer is now open to users' : 'Offer is closed', color: 'success' })
        } catch (error) {
            console.error('Failed to save the partner offer switch', error)
            setSnackbar({ open: true, message: 'Failed to save the switch', color: 'danger' })
        } finally {
            setSaving(null)
        }
    }

    if (!offers && !loadFailed) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1200 }}>
            <Typography level="h3" sx={{ mb: 1 }}>
                Mobile Partner Offers
            </Typography>
            <Typography level="body-sm" sx={{ mb: 3 }}>
                Partner cards (Octane fuel card, …) users apply for from a Home banner. The switch is per environment: Development and Production are separate,
                and an offer stays closed until it is turned on here. A row marked "Call made, outcome unknown" may be at the partner: look for the lead there
                by the user's email (source numeo-mobile) before choosing. The banner itself is made in Mobile Home Layout with the link{' '}
                <code>{partnerOfferDeeplink('<key>')}</code>.
            </Typography>

            {loadFailed ? (
                <Alert
                    color="danger"
                    endDecorator={
                        <Button size="sm" variant="soft" color="danger" onClick={() => void load()}>
                            Retry
                        </Button>
                    }
                >
                    Could not load the partner offers.
                </Alert>
            ) : null}

            {offers?.map((offer) => (
                <Card key={offer.definition.key} variant="outlined" sx={{ mb: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                        <Box>
                            <Typography level="title-lg">{offer.definition.partnerName}</Typography>
                            <Typography level="body-sm">
                                Banner link: <code>{partnerOfferDeeplink(offer.definition.key)}</code>
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography level="body-sm">{offer.enabled ? 'Open to users' : 'Closed'}</Typography>
                            <Switch
                                checked={offer.enabled}
                                disabled={saving === offer.definition.key}
                                onChange={(event) => void toggle(offer, event.target.checked)}
                            />
                        </Stack>
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Chip variant="soft" color="success">
                            In review: {offer.counts.in_review}
                        </Chip>
                        <Chip variant="soft" color="danger">
                            Failed: {offer.counts.failed}
                        </Chip>
                        <Chip variant="soft" color="warning">
                            Sending: {offer.counts.pending}
                        </Chip>
                    </Stack>

                    <ApplicationsTable
                        partnerName={offer.definition.partnerName}
                        offerKey={offer.definition.key}
                        fieldKeys={offer.definition.fields.map((field) => ({ key: field.key, label: field.label }))}
                        refreshToken={refreshToken}
                        onResolved={(message, ok) => setSnackbar({ open: true, message, color: ok ? 'success' : 'danger' })}
                    />
                    <Box>
                        <Button
                            size="sm"
                            variant="plain"
                            onClick={() => {
                                setRefreshToken((value) => value + 1)
                                void load()
                            }}
                        >
                            Refresh
                        </Button>
                    </Box>
                </Card>
            ))}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3500}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                color={snackbar.color}
                variant="soft"
            >
                {snackbar.message}
            </Snackbar>
        </Box>
    )
}
